import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import EmployeeDashboard from "@/pages/EmployeeDashboard";
import TimesheetEditor from "@/pages/TimesheetEditor";
import ManagerDashboard from "@/pages/ManagerDashboard";
import ManagerUsers from "@/pages/ManagerUsers";
import AdminConsole from "@/pages/AdminConsole";
import MonthlyHours from "@/pages/MonthlyHours";
import MonthEnd from "@/pages/MonthEnd";
import { currentMonth } from "@/lib/timesheet";
import { isAdmin, isApprover, homeFor } from "@/lib/roles";

function FullLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
    </div>
  );
}

function Protected({ children, managerOnly, employeeOnly, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (managerOnly && !isApprover(user)) return <Navigate to="/" replace />;
  if (adminOnly && !isAdmin(user)) return <Navigate to={homeFor(user)} replace />;
  if (employeeOnly && isApprover(user)) return <Navigate to="/approvals" replace />;
  return children;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (user) return <Navigate to={homeFor(user)} replace />;
  return <Login />;
}

function AppRoutes() {
  const location = useLocation();
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Protected employeeOnly><EmployeeDashboard /></Protected>} />
      <Route path="/timesheet/:id" element={<Protected employeeOnly><TimesheetEditor /></Protected>} />
      <Route path="/approvals" element={<Protected managerOnly><ManagerDashboard /></Protected>} />
      <Route path="/team" element={<Protected adminOnly><ManagerUsers /></Protected>} />
      <Route path="/hours" element={<Protected adminOnly><MonthlyHours /></Protected>} />
      <Route path="/admin" element={<Protected adminOnly><AdminConsole /></Protected>} />
      <Route path="/month" element={<Navigate to={`/month/${currentMonth()}`} replace />} />
      <Route path="/month/:ym" element={<Protected employeeOnly><MonthEnd /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
